/* src/components/heroManagementDashboard/aiWorkplace/AIWorkplaceRegister.js */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { assignUserRole } from "../../utils/roleManagement";
import { auth } from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { serverTimestamp } from "firebase/firestore";
import { setUser } from "../../../redux/actions";
import tpcAndUSLogo from "../../../images/AndUsLoginLogo.png";
import "./AIWorkplaceRegister.css";

/*──── CONFIG specific to AI in the Workplace flow ────*/
const CONFIG = {
  company: "TPC_Group",
  collectionBrand: "TechPerformanceCentre",
  systemName: "AIWorkplaceUsers",
  successRoute: "/ai-workplace-welcome",
  loginRoute: "/ai-workplace-login",
  defaultState: "AI Workplace User",
  claim: "ai-workplace-TPC_Group-TechPerformanceCentre",
};

export default function AIWorkplaceRegister() {
  /* form fields */
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [terms, setTerms] = useState(false);

  /* ui */
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [verifyStep, setVerify] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);

  const fn = getFunctions();
  const nav = useNavigate();
  const disp = useDispatch();
  const fsOp = httpsCallable(fn, "firestoreOperations");

  /*──────────────── register handler ───────────────*/
  const handleRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    if (!terms) return setErr("Please accept Terms & Privacy.");
    if (!(email && name && pw && confirmPw))
      return setErr("All fields are required.");
    if (pw.length < 6) return setErr("Password must be 6+ characters.");
    if (pw !== confirmPw) return setErr("Passwords do not match.");

    setBusy(true);
    try {
      console.log("[AIW] ▶ createUserWithEmailAndPassword");
      const { user } = await createUserWithEmailAndPassword(auth, email, pw);
      console.log("[AIW] Email registration user details:", {
        uid: user.uid,
        email: user.email,
        name: name,
      });
      disp(setUser({ uid: user.uid, email: user.email, name }));

      /* assign custom claim */
      await assignUserRole(user.uid, CONFIG.claim);
      console.log("[AIW] custom claim set:", CONFIG.claim);

      /* force-refresh ID-token */
      await auth.currentUser.getIdToken(true);

      /* stub doc in AIWorkplaceUsers */
      await fsOp({
        operation: "write",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: CONFIG.systemName,
        uniqueId: email,
        documentData: {
          email,
          name,
          state: CONFIG.defaultState,
          userType: "AI Workplace User",
          timestamp: serverTimestamp(),
        },
      });
      console.log("[AIW] AI Workplace User stub written");

      /* verification mail */
      await sendEmailVerification(user);
      setVerify(true);
      setMsg("Verification link sent! Check your inbox.");
    } catch (e) {
      console.error("[AIW] register error:", e);
      setErr(e.message || "Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── verify & redirect ───────────────*/
  const verifyRefresh = async () => {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await reload(auth.currentUser);
      if (auth.currentUser?.emailVerified) {
        console.log("[AIW] verified → redirect");
        nav(CONFIG.successRoute);
      } else {
        setErr("Email not verified yet. Please check your inbox.");
      }
    } catch (e) {
      console.error("[AIW] verify refresh error:", e);
      setErr("Error checking verification.");
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── Google Sign-In handler ───────────────*/
  const handleGoogleSignIn = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    setGoogleSignInLoading(true);

    try {
      console.log("[AIW] Starting Google sign-in");
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log("[AIW] Google sign-in successful:", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      });

      disp(
        setUser({
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email,
        })
      );

      // Check if user already exists in AIWorkplaceUsers
      const checkUser = httpsCallable(fn, "firestoreOperations");
      const checkResult = await checkUser({
        operation: "read",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: CONFIG.systemName,
        uniqueId: user.email,
      });

      const userExists = checkResult?.data?.exists;
      const existingUserProfile = checkResult?.data?.data;
      const displayName = user.displayName || user.email.split("@")[0];

      console.log("[AIW] User exists in AIWorkplaceUsers:", userExists);

      // Assign custom claim
      await assignUserRole(user.uid, CONFIG.claim);
      console.log("[AIW] custom claim set:", CONFIG.claim);

      // Force-refresh ID-token
      await auth.currentUser.getIdToken(true);

      // If user doesn't exist, create profile
      if (!userExists) {
        console.log("[AIW] Creating Google user profile in AIWorkplaceUsers");

        // Create profile with Google details
        await fsOp({
          operation: "write",
          company: CONFIG.company,
          collectionBrand: CONFIG.collectionBrand,
          systemName: CONFIG.systemName,
          uniqueId: user.email,
          documentData: {
            email: user.email,
            name: displayName,
            fullName: displayName,
            uid: user.uid,
            state: CONFIG.defaultState,
            userType: "AI Workplace User",
            authProvider: "google",
            emailVerified: true, // Google accounts are pre-verified
            timestamp: serverTimestamp(),
          },
        });
        console.log("[AIW] Profile created for Google user");
      } else {
        console.log("[AIW] Existing user found, logging in");
      }

      // Google accounts are automatically verified, redirect to success route
      console.log("[AIW] Redirecting to:", CONFIG.successRoute);
      nav(CONFIG.successRoute);
    } catch (e) {
      console.error("[AIW] Google sign-in error:", e);
      if (e.code === "auth/popup-closed-by-user") {
        setErr("Sign-in cancelled. Please try again.");
      } else if (e.code === "auth/popup-blocked") {
        setErr("Pop-up blocked. Please enable pop-ups and try again.");
      } else {
        setErr(e.message || "Google sign-in failed.");
      }
    } finally {
      setGoogleSignInLoading(false);
      setBusy(false);
    }
  };

  /*──────── render ────────*/
  return (
    <>
      <div
        className="ar-container"
        style={{
          position: "relative",
          filter: googleSignInLoading ? "blur(3px)" : "none",
          transition: "filter 0.3s ease",
        }}
      >
        {/* left banner */}
        <div className="ar-left">
          <div className="ar-logo-box">
            <img src={tpcAndUSLogo} alt="TPC" className="ar-logo" />
          </div>
          <div className="ar-banner-text">Welcome to TPC</div>
        </div>

        {/* right pane */}
        <div className="ar-right">
          {verifyStep ? (
            /* verification message */
            <div className="ar-verify-card">
              <h2 className="ar-title">Verify Your Email</h2>
              {err && <p className="ar-err">{err}</p>}
              {msg && <p className="ar-success">{msg}</p>}
              <p className="ar-help">Click below after you've verified.</p>
              <button
                className="ar-button"
                onClick={verifyRefresh}
                disabled={busy}
              >
                {busy ? (
                  <div className="ar-button-loader">
                    <div className="ar-spinner"></div>
                    <span>Verifying Email...</span>
                  </div>
                ) : (
                  "I've verified my email"
                )}
              </button>
            </div>
          ) : (
            /* main form */
            <form className="ar-form" onSubmit={handleRegister}>
              <h2 className="ar-title">Apply as an AI Applicant</h2>
              {err && <p className="ar-err">{err}</p>}
              <input
                type="text"
                placeholder="Full Name"
                className="ar-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="ar-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Enter your password"
                className="ar-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Confirm your password"
                className="ar-input"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
              />
              <div className="ar-check-wrap">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  className="ar-check"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  required
                />
                <label
                  htmlFor="terms-checkbox"
                  className="ar-check-label"
                  style={{ cursor: "pointer" }}
                >
                  I agree to&nbsp;
                  <a
                    href="https://techperformancecentre.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>
                  &nbsp;and&nbsp;
                  <a
                    href="https://techperformancecentre.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy
                  </a>
                  .
                </label>
              </div>

              <p
                style={{
                  color: "#f59e0b",
                  fontSize: "0.85rem",
                  textAlign: "center",
                  margin: "0 0 10px 0",
                  fontWeight: "500",
                  visibility: !terms ? "visible" : "hidden",
                  minHeight: "20px",
                }}
              >
                ⚠️ Please accept Terms & Privacy to continue
              </p>

              <button
                type="submit"
                className="ar-button"
                disabled={!terms || busy}
              >
                {busy ? (
                  <div className="ar-button-loader">
                    <div className="ar-spinner"></div>
                    <span>Registering...</span>
                  </div>
                ) : (
                  "Register"
                )}
              </button>

              <div className="ar-divider">
                <span>or</span>
              </div>

              <button
                type="button"
                className="ar-button ar-google-button"
                onClick={(e) => {
                  if (!terms) {
                    e.preventDefault();
                    e.stopPropagation();
                    setErr(
                      "Please accept Terms & Privacy before signing in with Google."
                    );
                    return;
                  }
                  handleGoogleSignIn();
                }}
                disabled={!terms || busy}
                style={{
                  opacity: !terms || busy ? 0.6 : 1,
                  cursor: !terms || busy ? "not-allowed" : "pointer",
                  pointerEvents: !terms || busy ? "none" : "auto",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>

              <p className="ar-small">
                Already registered? &nbsp;
                <Link to={CONFIG.loginRoute}>Log in</Link>
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Google Sign-In Loading Overlay - Outside of blurred container */}
      {googleSignInLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
              maxWidth: "400px",
              width: "90%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                style={{
                  animation: "spin 1s linear infinite",
                  marginRight: "12px",
                }}
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#4285F4"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="60"
                  strokeDashoffset="15"
                />
              </svg>
              <h3
                style={{
                  margin: 0,
                  color: "#333",
                  fontSize: "18px",
                }}
              >
                Signing in with Google...
              </h3>
            </div>
            <p
              style={{
                margin: 0,
                color: "#666",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Please complete the Google sign-in process in the popup window.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
